import { useFetcher, useSubmit as _useSubmit } from "react-router-dom";
import { Button, ButtonProps } from "../ui/button";
import { NavigationStatus } from "./navigation-status";
import { useEffect, useState } from "react";
import { ActionWrapper } from "@/utils/wretch";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
} from "../ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

interface ActionButtonProps<T = any> extends ButtonProps {
    action?: string;
    method?: "get" | "post" | "put" | "patch" | "delete";
    payload?: any;
    successNotification?: string;
    confirmTitle?: string;
    confirmMessage?: string;
    thenFn?: (response: ActionWrapper<T>) => void;
    successFn?: (response: ActionWrapper<T>) => void;
    errorFn?: (response: ActionWrapper<T>) => void;
    icon?: boolean;
    tooltip?: string;
}

export function ActionButton<T>({
    action = "",
    method = "post",
    payload,
    successNotification,
    confirmTitle,
    confirmMessage,
    thenFn,
    successFn,
    errorFn,
    icon = true,
    tooltip,
    children,
    ...props
}: ActionButtonProps<T>) {
    const fetcher = useFetcher<ActionWrapper<T>>();
    const [open, setOpen] = useState(false);
    const requiresConfirmation = Boolean(confirmTitle || confirmMessage);

    const onClick = () => {
        fetcher.submit(payload || {}, { method: method, action: action, encType: "application/json" });
    };

    const onConfirm = () => {
        onClick();
        setOpen(false);
    };

    useEffect(() => {
        if (fetcher.data) {
            setOpen(false);
            if (thenFn) {
                thenFn(fetcher.data);
            } else if (fetcher.data.success) {
                if (successFn) {
                    successFn(fetcher.data);
                } else if (successNotification) {
                    toast.success(successNotification);
                }
            } else {
                if (errorFn) {
                    errorFn(fetcher.data);
                } else {
                    toast.error(`Une erreur est survenue : ${fetcher.data.message}`);
                }
            }
        }
    }, [fetcher.data]);

    const isDisabled = fetcher.state != "idle" || props.disabled;

    const buttonContent = (
        <Button {...props} type="button" onClick={requiresConfirmation ? undefined : onClick} disabled={isDisabled}>
            <NavigationStatus fetcher={fetcher} icon={icon}>
                {children}
            </NavigationStatus>
        </Button>
    );

    const triggerChild = tooltip && isDisabled ? (
        <span className="inline-flex w-full cursor-not-allowed" aria-disabled="true">
            {buttonContent}
        </span>
    ) : (
        buttonContent
    );

    const maybeWrappedWithTooltip = tooltip ? (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>{triggerChild}</TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    ) : (
        buttonContent
    );

    return requiresConfirmation ? (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>{maybeWrappedWithTooltip}</AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{confirmTitle || "Êtes-vous sûr de vouloir continuer ?"}</AlertDialogTitle>
                    {confirmMessage && <AlertDialogDescription>{confirmMessage}</AlertDialogDescription>}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <NavigationStatus fetcher={fetcher} icon={icon}>
                        <AlertDialogAction onClick={onConfirm} disabled={isDisabled}>
                            Continuer
                        </AlertDialogAction>
                    </NavigationStatus>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    ) : (
        maybeWrappedWithTooltip
    );
}
